import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  startActivity,
  endActivity,
  updateActivityNote,
  reimputeActivity,
  reimputeActivities,
} from "../api/activities";

function invalidateActivityReads(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["tawa", "sessions", "open"] });
  void qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "tawa" &&
      q.queryKey[1] === "worker" &&
      q.queryKey[2] === "timeline",
  });
}

function useActivityMutation(
  mutationFn: typeof startActivity | typeof endActivity,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidateActivityReads(qc),
  });
}

export function useStartActivity() {
  return useActivityMutation(startActivity);
}

export function useEndActivity() {
  return useActivityMutation(endActivity);
}

export function useUpdateActivityNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      startEventId,
      note,
    }: {
      startEventId: string;
      note: string;
    }) => updateActivityNote(startEventId, note),
    onSuccess: () => invalidateActivityReads(qc),
  });
}

// Une réimputation change les jours, le temps sans projet et l'imputation que
// la feuille relit.
export function invalidateReimputationReads(
  qc: ReturnType<typeof useQueryClient>,
) {
  invalidateActivityReads(qc);
  void qc.invalidateQueries({ queryKey: ["tawa", "worker", "timesheets"] });
}

export function useReimputeActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      startEventId,
      ...body
    }: Parameters<typeof reimputeActivity>[1] & { startEventId: string }) =>
      reimputeActivity(startEventId, body),
    onSuccess: () => invalidateReimputationReads(qc),
  });
}

export function useReimputeActivities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reimputeActivities,
    onSuccess: () => invalidateReimputationReads(qc),
  });
}
